import React from 'react';
import { Box, Avatar, Paper, Typography, CircularProgress, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Message } from '../hooks/useChatMessages';

interface ChatLoadingIndicatorProps {
    type: 'typing' | 'roadmap';
    loadingProgress?: string;
    loadingTime?: number;
    messages: Message[];
}

const ChatLoadingIndicator: React.FC<ChatLoadingIndicatorProps> = ({
    type,
    loadingProgress,
    loadingTime = 0,
    messages
}) => {
    const { t, i18n } = useTranslation();

    // Helper to detect language from messages (reused logic)
    const detectLanguage = () => {
        // For general UI, check recent messages
        const recentMessages = messages.slice(-3);
        const recentText = recentMessages.map(m => m.content).join(' ');
        const isChinese = /[\u4e00-\u9fff]/.test(recentText);
        const isSpanish = /[áéíóúñü]/.test(recentText.toLowerCase());
        const isFrench = /[àâäéèêëïîôöùûüÿç]/.test(recentText.toLowerCase());

        if (isChinese) return 'zh';
        if (isSpanish) return 'es';
        if (isFrench) return 'fr';
        return 'en';
    };

    const getLoadingText = () => {
        if (loadingProgress) return loadingProgress;

        // Default thinking text
        const lang = detectLanguage();
        if (lang === 'zh') return '正在思考...';
        if (lang === 'es') return 'Pensando...';
        if (lang === 'fr') return 'Réfléchit...';
        return 'Thinking...';
    };

    const getRoadmapLoadingText = () => {
        // Use the last user message specifically for roadmap context
        const lastUserMessage = messages.find(m => m.role === 'user')?.content || '';
        const isChinese = /[\u4e00-\u9fff]/.test(lastUserMessage);
        const isSpanish = /[áéíóúñü]/.test(lastUserMessage.toLowerCase());
        const isFrench = /[àâäéèêëïîôöùûüÿç]/.test(lastUserMessage.toLowerCase());

        if (isChinese) return '正在创建宠物训练计划...';
        if (isSpanish) return 'Creando plan de entrenamiento para mascotas...';
        if (isFrench) return 'Création du plan d\'entraînement pour animaux...';
        return 'Creating pet training plan...';
    };

    const getTimeoutWarning = () => {
        const lang = detectLanguage();
        if (lang === 'zh') return '比平时用时更长。复杂的请求如路线图生成可能需要2分钟。';
        if (lang === 'es') return 'Está tomando más tiempo de lo usual. Las solicitudes complejas como la generación de mapas de ruta pueden tomar hasta 2 minutos.';
        if (lang === 'fr') return 'Cela prend plus de temps que d\'habitude. Les demandes complexes comme la génération de feuilles de route peuvent prendre jusqu\'à 2 minutes.';
        return 'This is taking longer than usual. Complex requests like roadmap generation may take up to 2 minutes.'; // Default English
    };

    if (type === 'roadmap') {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1 }}>
                <Avatar sx={{ width: 32, height: 32, bgcolor: 'warning.main' }}>
                    <img
                        src="/logo192.png"
                        alt="PetSnap Assistant"
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain'
                        }}
                    />
                </Avatar>
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, borderTopLeftRadius: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={16} />
                        <Typography variant="body2" color="text.secondary">
                            {getRoadmapLoadingText()}
                        </Typography>
                    </Box>
                </Paper>
            </Box>
        );
    }

    // Default 'typing' type
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1 }}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                <img
                    src="/logo192.png"
                    alt="PetSnap Assistant"
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain'
                    }}
                />
            </Avatar>
            <Paper elevation={1} sx={{ p: 2, borderRadius: 2, borderTopLeftRadius: 0 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Box sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: 'grey.400',
                                animation: 'pulse 1.5s ease-in-out infinite'
                            }} />
                            <Box sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: 'grey.400',
                                animation: 'pulse 1.5s ease-in-out infinite 0.2s'
                            }} />
                            <Box sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: 'grey.400',
                                animation: 'pulse 1.5s ease-in-out infinite 0.4s'
                            }} />
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                            {getLoadingText()}
                        </Typography>
                        {loadingTime > 0 && (
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                ({Math.round(loadingTime)}s)
                            </Typography>
                        )}
                    </Box>

                    {/* Timeout warning */}
                    {loadingTime > 45 && (
                        <Alert severity="info" sx={{ py: 0, px: 1, fontSize: '0.75rem' }}>
                            {getTimeoutWarning()}
                        </Alert>
                    )}
                </Box>
            </Paper>
        </Box>
    );
};

export default ChatLoadingIndicator;
